import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { bookingGuestScreenQuery, useResendGuestLink, type GuestScreen } from '@/entities/booking'
import { problemText } from '@/shared/api'
import { Icon, Popover, Skeleton } from '@/shared/ui'
import { GuestLinkFooter, type GuestLinkSent } from './GuestLinkFooter'
import s from './GuestScreenButton.module.css'

type GuestScreenButtonProps = {
  bookingId: string
}

/**
 * «Экран гостя» (prototype/Prototype.dc.html:2359) — что у гостя открыто ПРЯМО СЕЙЧАС. До этого
 * менеджер не мог ответить на вопрос никак, кроме «позвонить и спросить» (3.3).
 *
 * ⚠️ ТОЛЬКО ПРОСМОТР. Экран гостя — производная от сценария и контента, и второго редактора бота не
 * будет: он разошёлся бы с тем, что бот показывает на самом деле (6.12).
 *
 * ⚠️ ТЕКСТОВ ПАМЯТОК ЗДЕСЬ НЕТ — только названия. Показ памятки унаследовал бы проверку доступа,
 * рассчитанную на ГОСТЯ, и при отсутствующем тексте система пишет запись в переписку самого гостя:
 * менеджер, который просто «посмотрел», оставил бы след в чате с ним.
 *
 * Запрос — лениво, по открытию: состояние живое, и тянуть его на каждое открытие карточки значило
 * бы платить за то, на что чаще всего не смотрят.
 */
export function GuestScreenButton({ bookingId }: GuestScreenButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const screen = useQuery({ ...bookingGuestScreenQuery(bookingId), enabled: isOpen })
  // Отправка и её подтверждение живут ЗДЕСЬ, а не в подвале: подвал уходит вместе со скелетоном на
  // каждом перезапросе экрана (в том числе на возврате фокуса в окно), и подтверждение «ссылка
  // ушла» умирало бы ровно тогда, когда менеджер сходил проверить и вернулся — а не увидев его, он
  // нажмёт снова, и гость получит вторую ссылку вместо первой.
  const resend = useResendGuestLink(bookingId)
  const [sent, setSent] = useState<GuestLinkSent | null>(null)

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      align="end"
      width={326}
      trigger={
        <button
          type="button"
          className={s.trigger}
          title="Что гость видит у себя на экране прямо сейчас"
        >
          <Icon name="eye" size={13} strokeWidth={1.8} />
          Экран гостя
        </button>
      }
    >
      <div className={s.panel}>
        <p className={s.title}>Что видит гость сейчас</p>

        {/*
         * Скелетон и на ПЕРЕЗАПРОСЕ (`isFetching`), а не только на первой загрузке. Порядок
         * «данные → ошибка → скелетон» из соседних экранов здесь бы навредил: наблюдатель живёт,
         * пока открыта карточка, поэтому при повторном открытии в кеше лежит ПРОШЛОЕ чтение — и
         * показать его значило бы ответить не на тот вопрос. Поповер про «сейчас».
         */}
        {screen.isPending || screen.isFetching ? (
          <div className={s.skeleton}>
            <Skeleton width="100%" height={14} radius={6} />
            <Skeleton width="80%" height={14} radius={6} />
          </div>
        ) : (
          <>
            {screen.error ? (
              <p className={s.note} role="alert">
                {problemText(screen.error, 'Не удалось узнать, что у гостя на экране.')}
              </p>
            ) : screen.data ? (
              <GuestScreenBody screen={screen.data} />
            ) : null}

            {/* Подвал рисуем и при ошибке чтения экрана: переотправка живёт в сервисе доставки, и
                прятать её из-за молчания сервиса сценариев значило бы повторить ту же ошибку, от
                которой защищает признак `available`, — только этажом выше. */}
            {screen.error || screen.data ? (
              <GuestLinkFooter
                guestLink={screen.data?.guestLink ?? null}
                sent={sent}
                isPending={resend.isPending}
                errorText={
                  resend.error ? problemText(resend.error, 'Не удалось отправить ссылку.') : null
                }
                onResend={async () => {
                  // Прошлый успех снимаем ДО попытки: иначе рядом встанут «ушла гостю» от одной
                  // отправки и отказ от другой, и разобрать, что к чему, будет нельзя.
                  setSent(null)
                  try {
                    const result = await resend.mutateAsync()
                    setSent({ to: result.sentToLabel, coverage: result.coverageLabels })
                  } catch {
                    // Ошибку показывает строкой сам подвал; ловим, чтобы не было unhandled rejection.
                  }
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </Popover>
  )
}

function GuestScreenBody({ screen }: { screen: GuestScreen }) {
  return (
    <>
      {/*
       * ⚠️ Сердце этого экрана. «Не смогли узнать» и «бронь ничего не ждёт» — РАЗНЫЕ утверждения,
       * и без этой ветки сбой сервиса сценариев выглядел бы как пустая бронь, которой менеджер
       * поверит (3.3). Признак говорит про сервис сценариев, поэтому прячем именно то, что от него
       * зависит: чего ждёт бронь и что ей открыто. Чаты и режим диалога — из других сервисов, они
       * достоверны и при сбое (см. описание `available` в контракте).
       */}
      {screen.available ? (
        screen.waiting.length > 0 ? (
          <ul className={s.waiting} role="list">
            {screen.waiting.map((step, index) => (
              <li key={`${step.stepTitle}:${index}`} className={s.step}>
                {/* Срок — рядом с «чего ждём», а не отдельной строкой: без него «ждём оплату» не
                    отвечает на вопрос, пора ли вмешиваться. §7.1 обещает его и менеджеру, и в живом
                    экране гостя; до сих пор он доезжал только в дорожку («Ход брони»). Здесь он у
                    КАЖДОГО активного шага: при параллельных ветках их несколько, и общий срок
                    дорожки сказал бы не про тот. */}
                <span className={s.stepWait}>
                  {step.waitingFor}
                  {step.dueLabel ? <span className={s.stepDue}> {step.dueLabel}</span> : null}
                </span>
                <span className={s.stepName}>шаг «{step.stepTitle}»</span>
                {/* Пусто — не ошибка: менеджер не написал заголовок, бот подставит свой. Своими
                    словами это место не заполняем — у бота своя таблица подстановок (6.12). */}
                {step.guestTitle ? (
                  <span className={s.guestTitle}>Гость видит: «{step.guestTitle}»</span>
                ) : (
                  <span className={s.guestTitleEmpty}>Заголовок не задан — бот подставит свой</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={s.note}>Бронь ничего не ждёт — нового у гостя не появится.</p>
        )
      ) : (
        <p className={s.unavailable}>
          {screen.unavailableNote ?? 'Не удалось узнать, чего ждёт бронь.'}
        </p>
      )}

      <p className={s.eyebrow}>Кнопки в панели</p>
      {/* Кнопки называет СЕРВЕР — он один знает, что у гостя в панели. Своя строка здесь означала
          бы, что фронт наперёд знает набор кнопок бота; переименуй компания «Написать нам» — и
          поповер соврал бы. Берём с первого ждущего шага: панель у гостя одна. */}
      {panelButtons(screen).length > 0 ? (
        <div className={s.chips}>
          {panelButtons(screen).map((label) => (
            <span key={label} className={s.chip}>
              {label}
            </span>
          ))}
        </div>
      ) : (
        <p className={s.note}>
          {screen.available
            ? 'Кнопок в панели нет.'
            : 'Не смогли узнать, что открыто гостю.'}
        </p>
      )}

      {screen.menu.length > 0 ? (
        <>
          <p className={s.eyebrow}>Памятки, открытые сейчас</p>
          <ul className={s.memos} role="list">
            {screen.menu.map((memo) => (
              <li key={memo.role} className={s.memo}>
                <span className={s.memoName}>{memo.label}</span>
                {/* Срок — видимым текстом, а не тултипом: «до каких пор открыта» и есть ответ,
                    ради которого сюда смотрят, а тултип не достаётся ни с клавиатуры, ни с тача. */}
                {memo.validityLabel ? (
                  <span className={s.memoValidity}>{memo.validityLabel}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <p className={s.eyebrow}>Чаты с ботом</p>
      {screen.chats.length > 0 ? (
        <ul className={s.chats} role="list">
          {screen.chats.map((chat) => (
            <li key={chat.messenger} className={s.chat}>
              {/* «Панели нет» — настоящее состояние, а не пробел в данных: гость открыл бота, но на
                  экране у него ничего. Красим точкой, чтобы это читалось с одного взгляда. */}
              <span
                className={s.dot}
                data-empty={chat.hasPanel ? undefined : 'true'}
                aria-hidden="true"
              />
              <span className={s.chatText}>
                <span className={s.chatName}>{chat.messengerLabel}</span>
                <span className={s.chatState}> — {chat.stateLabel}</span>
                {/* Панель, которую бот пока не может править (VK): у гостя она может быть
                    устаревшей, и 3.3 просит об этом СКАЗАТЬ, а не показывать её как свежую. */}
                {chat.panelEditable === false ? (
                  <span className={s.chatStale}>
                    Правки уезжают новыми сообщениями — панель у гостя может быть устаревшей.
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={s.note}>Гость ещё не открывал бота ни в одном мессенджере.</p>
      )}

      <p className={s.mode}>{screen.dialogMode.label}</p>
    </>
  )
}

/** Кнопки панели гостя — с ждущего шага. Шагов может не быть (бронь ничего не ждёт) — тогда пусто. */
function panelButtons(screen: GuestScreen): ReadonlyArray<string> {
  return screen.waiting.find((step) => (step.buttons ?? []).length > 0)?.buttons ?? []
}
